import React from 'react';
import PropTypes from 'prop-types';
import dayjs from 'utils/dayjs-duration';
import withConfig from 'config/withConfig';
import FormatUtils from 'utils/format';
import {PROPTYPE_FACILITY_SUMMARY} from 'proptypes';

const buildFacilitySchema = ({address, cityUrl}) => {
    const {streetAddress, city, state, latitude, longitude} = address;

    return {
        '@type': 'ParkingFacility',
        address: {
            '@type': 'PostalAddress',
            streetAddress,
            addressLocality: city,
            addressRegion: state,
        },
        geo: {
            '@type': 'GeoCoordinates',
            latitude,
            longitude,
        },
        containedInPlace: {
            '@type': 'City',
            name: city,
            url: cityUrl,
        },
    };
};

const buildAvailableOfferSchema = ({spot, starts, ends, duration, cityUrl}) => {
    const {
        selectedRate: {
            advertisedPrice: {
                value: displayPriceRaw,
                currencyCode: currencyType,
            },
            fullUrl,
        },
        title,
    } = spot;
    const {displayPrice} = FormatUtils.price(displayPriceRaw);
    const description = `Reserved Parking at ${title} for ${duration} Hour${
        duration === 1 ? '' : 's'
    }`;

    return {
        '@type': 'Offer',
        name: 'Parking',
        url: fullUrl,
        description,
        priceCurrency: currencyType,
        price: displayPrice,
        availability: 'http://schema.org/InStock',
        validFrom: starts,
        validThrough: ends,
        offeredBy: {
            '@type': 'Organization',
            name: 'SpotHero',
            url: 'https://spothero.com',
        },
        availableAtOrFrom: buildFacilitySchema({
            address: spot.addresses[0],
            cityUrl,
        }),
    };
};

const buildUnvailableOfferSchema = ({spot, duration, cityUrl}) => {
    const {title} = spot;
    const description = `Reserved Parking at ${title} for ${duration} Hour${
        duration === 1 ? '' : 's'
    }`;

    return {
        '@type': 'Offer',
        name: 'Parking',
        description,
        availability: 'http://schema.org/SoldOut',
        offeredBy: {
            '@type': 'Organization',
            name: 'SpotHero',
            url: 'https://spothero.com',
        },
        availableAtOrFrom: buildFacilitySchema({
            address: spot.addresses[0],
            cityUrl,
        }),
    };
};

export const buildTransientAggregateOfferSchema = ({
    spots,
    starts,
    ends,
    citySlug,
    siteUrl,
    canonicalUrl,
}) => {
    const cityUrl = `${siteUrl}/city/${citySlug}`;
    const endsTime = dayjs(ends);
    const startsTime = dayjs(starts);
    const duration = parseInt(
        dayjs.duration(endsTime.diff(startsTime)).asHours(),
        10
    );

    return {
        '@context': 'https://schema.org/',
        '@type': 'AggregateOffer',
        url: canonicalUrl,
        offerCount: spots.length,
        validFrom: starts,
        validThrough: ends,
        offers: spots.reduce((availableOffers, spot) => {
            if (spot.availability.available) {
                availableOffers.push(
                    buildAvailableOfferSchema({
                        spot,
                        starts,
                        ends,
                        duration,
                        cityUrl,
                    })
                );
            } else {
                availableOffers.push(
                    buildUnvailableOfferSchema({
                        spot,
                        starts,
                        ends,
                        duration,
                        cityUrl,
                    })
                );
            }

            return availableOffers;
        }, []),
    };
};

const TransientOfferSchema = ({
    spots,
    starts,
    ends,
    siteUrl,
    citySlug,
    canonicalUrl,
}) => {
    return (
        <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
                __html: JSON.stringify(
                    buildTransientAggregateOfferSchema({
                        spots,
                        starts,
                        ends,
                        siteUrl,
                        citySlug,
                        canonicalUrl,
                    })
                ),
            }}
        />
    );
};

TransientOfferSchema.propTypes = {
    spots: PropTypes.arrayOf(PROPTYPE_FACILITY_SUMMARY).isRequired,
    starts: PropTypes.string.isRequired,
    ends: PropTypes.string.isRequired,
    citySlug: PropTypes.string.isRequired,
    siteUrl: PropTypes.string.isRequired,
    canonicalUrl: PropTypes.string.isRequired,
};

export default withConfig(['siteUrl'])(TransientOfferSchema);
